import React from 'react';
import {
  ATMDivider,
  ATMForm,
  ATMGrid,
  ATMHeader,
  ATMSegment,
} from 'shared-it-appmod-ui';
import FieldReadOnlyCommment from 'src/components/atoms/field/field-readonly-comment/field-readonly-comment.component';
import FieldReadOnly from 'src/components/atoms/field/field-readonly/field-readonly.component';
import FlagReadOnly from 'src/components/atoms/flag-readonly/flag-readonly.component';
import FileManager from 'src/components/molecules/file-manager/file-manager.component';
import Lang from 'src/libraries/language';
import { IRequest } from 'src/models/request.model';
import styles from '../flight-request-drone.module.scss';

type IProps = {
  data: IRequest;
};

const FlightRequestDroneSDGEReviewComponent: React.FC<IProps> = ({
  data: { droneFlightSDGE },
}) => {
  return (
    <ATMSegment color="teal" className={styles.wrapper}>
      <ATMHeader content={Lang.TTL_DRONE_FLIGHT_SCG} />
      <ATMForm>
        <ATMGrid columns={4}>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FLIGHT_TYPE}>
                {droneFlightSDGE.flightType}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_PROJECT}>
                {droneFlightSDGE.project}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
          {droneFlightSDGE.purpose && (
            <ATMGrid.Row>
              <ATMGrid.Column width="16">
                <FieldReadOnly
                  label={Lang.LBL_PURPOSE}
                  className={styles.wrapText}
                >
                  {droneFlightSDGE.purpose}
                </FieldReadOnly>
              </ATMGrid.Column>
            </ATMGrid.Row>
          )}
          <ATMGrid.Row columns="equal">
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_ADDRESS_CROSS_STREET}>
                {droneFlightSDGE.destination}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row columns="equal">
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FLIGHT_DISTRICT_DEPARTMENT}>
                {droneFlightSDGE.district}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_FACILITY_ASSET_NUMBER}>
                {droneFlightSDGE.asset}
              </FieldReadOnly>
            </ATMGrid.Column>
            <ATMGrid.Column>
              <FieldReadOnly label={Lang.LBL_PIPELINE_NUMBER}>
                {droneFlightSDGE.circuit}
              </FieldReadOnly>
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_INFORMATION}
        </ATMHeader>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FlagReadOnly
              value={droneFlightSDGE.nightFlag}
              label={Lang.LBL_NIGHT_FLIGHT}
            />
          </ATMGrid.Column>
          {droneFlightSDGE?.isOldFlight ? (
            <>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.sensitiveFlag}
                  label={Lang.LBL_SENSITIVE_CUSTOMER}
                  no
                />
              </ATMGrid.Column>
            </>
          ) : (
            <>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.airspaceFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_1}
                />

                {droneFlightSDGE.airspaceFlag && (
                  <FileManager
                    files={droneFlightSDGE?.airspaceFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.safetyFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_2}
                  no={false}
                  na
                />

                {droneFlightSDGE.safetyFlag && (
                  <FileManager
                    files={droneFlightSDGE?.safetyFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.sensitiveFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_3}
                  no={false}
                  na
                />
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.landFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_4}
                />

                {droneFlightSDGE.landFlag && (
                  <FileManager
                    files={droneFlightSDGE?.landFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>

              <ATMGrid.Column>
                <FlagReadOnly
                  value={droneFlightSDGE.culturalFlag}
                  label={Lang.LBL_DRONE_OTHER_QUESTION_5}
                />

                {droneFlightSDGE.culturalFlag && (
                  <FileManager
                    files={droneFlightSDGE?.culturalFiles}
                    inline
                    editable={false}
                  />
                )}
              </ATMGrid.Column>
            </>
          )}
        </ATMGrid>
        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_BRIEF_JOB_DESCRIPTION}
        </ATMHeader>
        <p>{Lang.MSG_BRIEF_DESCRIPTION}</p>

        <ATMGrid columns={1}>
          <ATMGrid.Column>
            <FieldReadOnlyCommment label={Lang.LBL_DESCRIPTION_COMMENT}>
              {droneFlightSDGE.jobDesc}
            </FieldReadOnlyCommment>
          </ATMGrid.Column>
        </ATMGrid>

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_FLIGHT_AREA}
        </ATMHeader>
        <ATMForm.Field>
          <FileManager
            files={droneFlightSDGE.flightAreaFiles}
            editable={false}
            inline
          />
        </ATMForm.Field>
        <ATMDivider />

        <ATMHeader size="small" className="text-underline">
          {Lang.LBL_OTHER_SPECIAL_COMMENTS}
        </ATMHeader>

        <ATMGrid>
          <ATMGrid.Column>
            <FieldReadOnlyCommment>
              {droneFlightSDGE.comments}
            </FieldReadOnlyCommment>
          </ATMGrid.Column>
        </ATMGrid>
      </ATMForm>
    </ATMSegment>
  );
};

export default FlightRequestDroneSDGEReviewComponent;
